import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, Clock, Instagram, Facebook, Ghost, MessageSquare } from "lucide-react";
import { BRAND } from "@/lib/api";
import FaqSection from "@/components/FaqSection";
import { useSite } from "@/context/SiteContext";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/simulation", label: "Simulation de gain" },
  { to: "/planning", label: "Planning" },
  { to: "/rendez-vous", label: "Rendez-vous" },
  { to: "/contact", label: "Contact" },
];

export const RedButton = ({ children, className = "", ...props }) => (
  <button
    className={`font-head text-base md:text-lg font-extrabold uppercase tracking-wider bg-sared text-white px-7 py-4 border border-sared transition-colors duration-200 hover:bg-black hover:border-black focus:outline-none focus:ring-2 focus:ring-sared ${className}`}
    {...props}
  >
    {children}
  </button>
);

export const GhostButton = ({ children, className = "", ...props }) => (
  <button
    className={`font-head text-base md:text-lg font-extrabold uppercase tracking-wider bg-transparent text-white px-7 py-4 border border-zinc-600 transition-colors duration-200 hover:border-sared hover:text-sared focus:outline-none focus:ring-2 focus:ring-sared ${className}`}
    {...props}
  >
    {children}
  </button>
);

export const SectionTitle = ({ overline, title, className = "" }) => (
  <div className={`mb-10 ${className}`}>
    {overline && <div className="overline mb-3" data-testid="section-overline">{overline}</div>}
    <h2 className="text-4xl sm:text-5xl lg:text-6xl leading-[0.9] text-white">{title}</h2>
    <div className="mt-4 h-[3px] w-24 bg-sared" />
  </div>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-50 bg-black/90 backdrop-blur-md border-b border-sared" data-testid="site-header">
      <div className="mx-auto max-w-[1400px] px-4 md:px-8">
        <div className="flex items-center justify-between h-[76px] gap-4">
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <img src={BRAND.logo} alt="Logo SHRAL'AUTO" className="h-12 w-12 object-cover border border-sared" />
            <div className="leading-none">
              <div className="font-head text-2xl md:text-3xl font-black tracking-tight text-white">
                SHRAL<span className="text-sared">’</span>AUTO
              </div>
              <div className="font-mono-tech text-[9px] tracking-[0.22em] text-zinc-500 mt-1 hidden sm:block whitespace-nowrap">
                REPROGRAMMATION • PERFORMANCE
              </div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-1" data-testid="desktop-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) =>
                  `font-head text-sm font-bold uppercase tracking-wide whitespace-nowrap px-2.5 py-2 border-b-2 transition-colors duration-200 ${
                    isActive ? "text-sared border-sared" : "text-zinc-300 border-transparent hover:text-white hover:border-zinc-600"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/rendez-vous" className="hidden md:block" data-testid="header-rdv-cta">
              <RedButton className="px-4 py-3 text-sm whitespace-nowrap">Prendre rendez-vous</RedButton>
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              className="xl:hidden p-3 border border-zinc-700 text-white hover:border-sared transition-colors"
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-black border-t border-zinc-800" data-testid="mobile-nav">
          <div className="px-4 py-4 grid gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) =>
                  `font-head text-xl font-bold uppercase tracking-wide py-3 px-3 border-l-2 ${
                    isActive ? "text-sared border-sared bg-zinc-900" : "text-zinc-200 border-zinc-800"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => {
  const { settings, hoursText } = useSite();
  const s = settings || {};
  return (
    <footer className="bg-black border-t border-sared mt-24" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-14 grid gap-12 md:grid-cols-4">
        <div>
          <div className="font-head text-3xl font-black text-white mb-2">SHRAL<span className="text-sared">’</span>AUTO</div>
          <div className="font-mono-tech text-[10px] tracking-[0.2em] text-sared mb-5">{s.tagline}</div>
          <p className="text-sm text-zinc-400 leading-relaxed">{s.slogan}</p>
        </div>

        <div>
          <h4 className="font-head text-lg text-white mb-4">Coordonnées</h4>
          <ul className="space-y-3 text-sm text-zinc-400">
            <li className="flex gap-3"><Phone size={16} className="text-sared shrink-0 mt-0.5" />
              <a href={`tel:${String(s.phone || "").replace(/\s/g, "")}`} className="hover:text-sared" data-testid="footer-phone">{s.phone}</a></li>
            <li className="flex gap-3"><MessageSquare size={16} className="text-sared shrink-0 mt-0.5" />
              <a href={`sms:${String(s.phone || "").replace(/\s/g, "")}`} className="hover:text-sared" data-testid="footer-sms">Envoyer un SMS</a></li>
            <li className="flex gap-3"><Mail size={16} className="text-sared shrink-0 mt-0.5" />
              <a href={`mailto:${s.email || ""}`} className="hover:text-sared" data-testid="footer-email">{s.email}</a></li>
            <li className="flex gap-3"><Clock size={16} className="text-sared shrink-0 mt-0.5" />
              <span className="whitespace-pre-line" data-testid="footer-hours">{hoursText || s.hours}</span></li>
          </ul>
        </div>

        <div>
          <h4 className="font-head text-lg text-white mb-4">Navigation</h4>
          <ul className="grid grid-cols-2 gap-2 text-sm text-zinc-400">
            {NAV.map((n) => (
              <li key={n.to}><Link to={n.to} className="hover:text-sared">{n.label}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-head text-lg text-white mb-4">Informations</h4>
          <ul className="space-y-2 text-sm text-zinc-400">
            <li><Link to="/mon-rendez-vous" className="hover:text-sared" data-testid="footer-manage-rdv">Modifier / annuler un RDV</Link></li>
            <li><Link to="/mentions-legales" className="hover:text-sared">Mentions légales</Link></li>
            <li><Link to="/confidentialite" className="hover:text-sared">Politique de confidentialité</Link></li>
            <li><Link to="/cgv" className="hover:text-sared">Conditions générales</Link></li>
            <li><Link to="/admin/login" className="hover:text-sared font-bold text-white" data-testid="footer-admin-link">Espace gestion (garage)</Link></li>
          </ul>
          <div className="flex gap-3 mt-6">
            {[{ Icon: Instagram, url: s.social_instagram, label: "Instagram" },
              { Icon: Ghost, url: s.social_snapchat, label: "Snapchat" },
              { Icon: Facebook, url: s.social_facebook, label: "Facebook" }]
              .filter(({ url }) => url)
              .map(({ Icon, url, label }) => (
              <a key={label} href={url} target="_blank" rel="noreferrer"
                 className="p-3 border border-zinc-800 text-zinc-400 hover:border-sared hover:text-sared transition-colors"
                 aria-label={label} data-testid={`social-${label.toLowerCase()}`}>
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>
      </div>
      <div className="border-t border-zinc-900 py-5 text-center font-mono-tech text-[10px] tracking-[0.2em] text-zinc-600">
        © {new Date().getFullYear()} SHRAL’AUTO — TOUS DROITS RÉSERVÉS
      </div>
    </footer>
  );
};

const MobileCta = () => {
  const { settings } = useSite();
  const tel = String(settings?.phone || "").replace(/\s/g, "");
  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 grid grid-cols-[1fr,auto] border-t-2 border-black">
      <Link to="/rendez-vous"
            className="bg-sared text-white font-head text-xl font-black uppercase tracking-wider py-4 text-center"
            data-testid="mobile-sticky-rdv">
        Prendre rendez-vous
      </Link>
      <a href={`sms:${tel}`} data-testid="mobile-sticky-sms"
         className="bg-black text-white font-head text-sm font-black uppercase tracking-wider px-5 flex items-center gap-2 border-l-2 border-black">
        <MessageSquare size={16} className="text-sared" /> SMS
      </a>
    </div>
  );
};

const Layout = ({ children }) => (
  <div className="min-h-screen bg-black flex flex-col">
    <Header />
    <main className="flex-1 pb-20 md:pb-0">{children}</main>
    <FaqSection />
    <Footer />
    <MobileCta />
  </div>
);

export default Layout;
